import React from 'react';
import { ChevronDown, Grid2X2, List } from 'lucide-react';
import { cn } from '@/utils/cn';

interface ViewControlsProps {
  viewMode: 'grid' | 'list';
  onViewModeChange: (mode: 'grid' | 'list') => void;
  sortBy?: 'newest' | 'popular';
  onSortChange?: (sort: 'newest' | 'popular') => void;
  showSort?: boolean;
  className?: string;
}

const ViewControls: React.FC<ViewControlsProps> = ({
  viewMode,
  onViewModeChange,
  sortBy,
  onSortChange,
  showSort = true,
  className,
}) => {
  const [showSortMenu, setShowSortMenu] = React.useState(false);

  const sortOptions = [
    { value: 'newest' as const, label: 'جدیدترین' },
    { value: 'popular' as const, label: 'محبوب‌ترین' },
  ];

  const currentSortLabel = sortOptions.find(opt => opt.value === sortBy)?.label || 'جدیدترین';

  const toggleViewMode = () => {
    onViewModeChange(viewMode === 'grid' ? 'list' : 'grid');
  };

  // Lock body scroll when modal is open
  React.useEffect(() => {
    if (showSortMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showSortMenu]);

  return (
    <div className={cn('flex items-center justify-between gap-3', className)}>
      <div className="relative">
        {showSort && (
          <>
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className="flex items-center gap-2 font-persian text-sm font-semibold text-gray-700 transition-colors"
            >
              <span>{currentSortLabel}</span>
              <ChevronDown className={cn('w-4 h-4 transition-transform', showSortMenu && 'rotate-180')} />
            </button>

            {showSortMenu && (
              <div
                className="fixed inset-0 z-[9999] flex items-end justify-center"
                style={{ top: 0, left: 0, width: '100%', height: '100vh' }}
              >
                {/* Backdrop */}
                <div
                  className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                  onClick={() => setShowSortMenu(false)}
                />

                {/* Modal Content */}
                <div className="relative w-full max-w-md bg-white rounded-t-3xl shadow-2xl overflow-hidden animate-slide-up" dir="rtl">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-base font-bold text-gray-800 font-persian text-right">مرتب‌سازی بر اساس</h3>
                  </div>

                  {/* Options */}
                  <div className="px-4 py-2">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          onSortChange?.(option.value);
                          setShowSortMenu(false);
                        }}
                        className={cn(
                          'w-full px-4 py-4 text-right font-persian text-sm transition-all rounded-xl mb-2',
                          sortBy === option.value
                            ? 'bg-indigo-50 text-indigo-600 font-bold border-2 border-indigo-200'
                            : 'text-gray-700 hover:bg-gray-50 font-medium'
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <span>{option.label}</span>
                          {sortBy === option.value && (
                            <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Close Button */}
                  <div className="px-4 pb-6 pt-2">
                    <button
                      onClick={() => setShowSortMenu(false)}
                      className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-persian font-semibold rounded-xl transition-colors"
                    >
                      بستن
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <button
        onClick={toggleViewMode}
        className="transition-opacity hover:opacity-70"
        aria-label={viewMode === 'grid' ? 'تغییر به نمایش لیستی' : 'تغییر به نمایش شبکه‌ای'}
      >
        {viewMode === 'grid' ? (
          <Grid2X2 className="w-6 h-6 text-black" strokeWidth={2.2} />
        ) : (
          <List className="w-6 h-6 text-black" strokeWidth={2.2} />
        )}
      </button>
    </div>
  );
};

export default ViewControls;
